import { useEffect, useId, useRef, useState } from "react";
import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import {
  EMPTY_CONTACT,
  FIELD_LABELS,
  MAX_LENGTHS,
  TURNSTILE_SITE_KEY,
  submitContact,
  validateContact,
  type ContactField,
  type ContactValues,
  type FieldErrors,
} from "../../lib/contactForm";
import { openConsentPreferences } from "../../lib/consent";
import { useConsent } from "../../hooks/useConsent";
import { Turnstile } from "./Turnstile";

const inputClass =
  "w-full bg-primary-foreground/10 border rounded-lg px-4 py-2.5 text-sm text-primary-foreground placeholder:text-primary-foreground/50 transition-colors";

/** Border carries the field's validity, so it needs 3:1 either way (1.4.11). */
const borderClass = (invalid: boolean) =>
  invalid ? "border-error-light" : "border-primary-foreground/45";

type Status =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "sent" }
  | { kind: "error"; message: string };

export function ContactForm() {
  const { marketing: marketingAllowed } = useConsent();

  const [values, setValues] = useState<ContactValues>(EMPTY_CONTACT);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [status, setStatus] = useState<Status>({ kind: "idle" });
  const [emailOptIn, setEmailOptIn] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileNonce, setTurnstileNonce] = useState(0);

  const summaryRef = useRef<HTMLDivElement>(null);
  const sentRef = useRef<HTMLDivElement>(null);
  const mountedAt = useRef(Date.now());
  const uid = useId();

  const fieldId = (f: ContactField) => `${uid}-${f}`;
  const errorId = (f: ContactField) => `${uid}-${f}-error`;
  const hintId = (f: ContactField) => `${uid}-${f}-hint`;

  // Withdrawing marketing consent has to clear an opt-in already ticked.
  useEffect(() => {
    if (!marketingAllowed) setEmailOptIn(false);
  }, [marketingAllowed]);

  const errorList = (Object.keys(errors) as (ContactField | "captcha")[]).filter(
    (k) => errors[k],
  );

  // WCAG 3.3.1 — move focus to the summary so the failure is announced and the
  // user lands next to the list of what needs fixing.
  useEffect(() => {
    if (errorList.length > 0) summaryRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  useEffect(() => {
    if (status.kind === "sent") sentRef.current?.focus();
  }, [status.kind]);

  const setField = (field: ContactField, value: string) => {
    setValues((v) => ({ ...v, [field]: value }));
    // Clear a field's error as soon as it is touched again; re-validated on submit.
    setErrors((e) => (e[field] ? { ...e, [field]: undefined } : e));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (status.kind === "submitting") return;

    const found = validateContact(values);
    if (!turnstileToken) {
      found.captcha = "Complete the spam protection check below.";
    }
    if (Object.keys(found).length > 0) {
      setErrors(found);
      setStatus({ kind: "idle" });
      return;
    }

    setErrors({});
    setStatus({ kind: "submitting" });

    const result = await submitContact({
      values,
      emailMarketingConsent: marketingAllowed && emailOptIn,
      turnstileToken,
      elapsedMs: Date.now() - mountedAt.current,
    });

    if (result.ok) {
      setValues(EMPTY_CONTACT);
      setEmailOptIn(false);
      setStatus({ kind: "sent" });
      return;
    }

    // A consumed token cannot be reused — force a fresh challenge before retry.
    setTurnstileToken(null);
    setTurnstileNonce((n) => n + 1);
    if (result.fieldErrors) setErrors(result.fieldErrors);
    setStatus({ kind: "error", message: result.message });
  };

  if (status.kind === "sent") {
    return (
      <div
        ref={sentRef}
        tabIndex={-1}
        role="status"
        className="flex items-start gap-3 rounded-xl bg-primary-foreground/10 border border-primary-foreground/45 p-5"
      >
        <CheckCircle2
          className="mt-0.5 h-5 w-5 shrink-0 text-accent-light"
          aria-hidden="true"
        />
        <div>
          <p className="font-semibold text-primary-foreground">Message sent</p>
          <p className="mt-1 text-sm leading-relaxed text-primary-foreground/75">
            Thank you for reaching out. Melissa will be in touch soon. If it is
            urgent, please reach out by phone or social media.
          </p>
          <button
            type="button"
            onClick={() => {
              mountedAt.current = Date.now();
              setStatus({ kind: "idle" });
            }}
            className="mt-3 text-sm font-semibold text-accent-light underline decoration-accent-light/40 underline-offset-2 hover:decoration-accent-light"
          >
            Send another message
          </button>
        </div>
      </div>
    );
  }

  const submitting = status.kind === "submitting";

  return (
    <form className="space-y-4" onSubmit={handleSubmit} noValidate>
      {/* WCAG 3.3.1 — one summary, focusable, announced on submit failure. */}
      {errorList.length > 0 && (
        <div
          ref={summaryRef}
          tabIndex={-1}
          role="alert"
          className="rounded-xl border border-error-light bg-error-light/15 p-4"
        >
          <p className="flex items-center gap-2 text-sm font-semibold text-error-light">
            <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
            Please fix {errorList.length === 1 ? "this" : `these ${errorList.length}`} before
            sending
          </p>
          <ul className="mt-2 space-y-1 pl-6 list-disc">
            {errorList.map((key) => (
              <li key={key} className="text-sm text-primary-foreground/85">
                {key === "captcha" ? (
                  errors.captcha
                ) : (
                  <a
                    href={`#${fieldId(key)}`}
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById(fieldId(key))?.focus();
                    }}
                    className="underline decoration-primary-foreground/40 underline-offset-2 hover:decoration-primary-foreground"
                  >
                    {errors[key]}
                  </a>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {status.kind === "error" && errorList.length === 0 && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-xl border border-error-light bg-error-light/15 p-4 text-sm text-primary-foreground/85"
        >
          <AlertCircle
            className="mt-0.5 h-4 w-4 shrink-0 text-error-light"
            aria-hidden="true"
          />
          {status.message}
        </div>
      )}

      <p className="text-xs text-primary-foreground/60">
        Fields marked <span aria-hidden="true">*</span>
        <span className="sr-only">required</span> are required.
      </p>

      <div className="grid grid-cols-2 gap-3">
        <Field
          field="firstName"
          autoComplete="given-name"
          placeholder="Jane"
          required
          value={values.firstName}
          error={errors.firstName}
          onChange={setField}
          ids={{ fieldId, errorId, hintId }}
        />
        <Field
          field="lastName"
          autoComplete="family-name"
          placeholder="Smith"
          required
          value={values.lastName}
          error={errors.lastName}
          onChange={setField}
          ids={{ fieldId, errorId, hintId }}
        />
      </div>

      <Field
        field="email"
        type="email"
        autoComplete="email"
        placeholder="jane@example.com"
        required
        value={values.email}
        error={errors.email}
        onChange={setField}
        ids={{ fieldId, errorId, hintId }}
      />

      <Field
        field="childAge"
        autoComplete="off"
        placeholder="e.g. 2 years, 8 months"
        hint="Optional. Helps us match your child with the right starting point."
        value={values.childAge}
        error={errors.childAge}
        onChange={setField}
        ids={{ fieldId, errorId, hintId }}
      />

      <Field
        field="message"
        multiline
        placeholder="Tell us a little about your child's communication concerns and what you are hoping to work on..."
        required
        value={values.message}
        error={errors.message}
        onChange={setField}
        ids={{ fieldId, errorId, hintId }}
      />

      {/* Honeypot. Hidden from sighted users and from assistive tech, and taken
          out of the tab order, so only a bot fills it. Deliberately not
          display:none — some bots skip those. */}
      <div aria-hidden="true" className="absolute left-[-9999px] w-px h-px overflow-hidden">
        <label htmlFor={`${uid}-company`}>Company</label>
        <input
          id={`${uid}-company`}
          name="company"
          type="text"
          tabIndex={-1}
          autoComplete="off"
        />
      </div>

      {marketingAllowed ? (
        <label
          htmlFor={`${uid}-optin`}
          className="flex items-start gap-2.5 cursor-pointer pt-1"
        >
          <input
            id={`${uid}-optin`}
            type="checkbox"
            checked={emailOptIn}
            onChange={(e) => setEmailOptIn(e.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 rounded border-primary-foreground/45 bg-primary-foreground/10 accent-accent"
          />
          <span className="text-xs leading-relaxed text-primary-foreground/75">
            Email me practice news, developmental tips, and schedule openings.
            You can unsubscribe at any time.
          </span>
        </label>
      ) : (
        <p className="text-xs leading-relaxed text-primary-foreground/75 pt-1">
          Marketing email is switched off in your{" "}
          <button
            type="button"
            onClick={openConsentPreferences}
            className="font-semibold text-accent-light underline decoration-accent-light/40 underline-offset-2 hover:decoration-accent-light"
          >
            privacy preferences
          </button>
          , so this message will only be used to reply to you.
        </p>
      )}

      {TURNSTILE_SITE_KEY ? (
        <div className="pt-1">
          <Turnstile
            siteKey={TURNSTILE_SITE_KEY}
            action="contact"
            onToken={(token) => {
              setTurnstileToken(token);
              if (token) setErrors((e) => (e.captcha ? { ...e, captcha: undefined } : e));
            }}
            resetSignal={turnstileNonce}
          />
        </div>
      ) : (
        <p className="text-xs text-primary-foreground/60">
          Spam protection is not configured yet.
        </p>
      )}

      <button
        type="submit"
        disabled={submitting}
        aria-busy={submitting}
        className="w-full bg-accent text-accent-foreground font-semibold py-3.5 rounded-full hover:bg-accent/85 transition-colors text-sm disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {submitting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
        {submitting ? "Sending…" : "Request a Consultation"}
      </button>

      {/* WCAG 4.1.3 — progress is announced without stealing focus. */}
      <p role="status" aria-live="polite" className="sr-only">
        {submitting ? "Sending your message." : ""}
      </p>

      <p className="text-xs leading-relaxed text-primary-foreground/60">
        Please do not include sensitive medical details in this form. We will
        collect what we need securely once we are in touch.
      </p>
    </form>
  );
}

function Field({
  field,
  value,
  error,
  onChange,
  ids,
  type = "text",
  autoComplete,
  placeholder,
  hint,
  required = false,
  multiline = false,
}: {
  field: ContactField;
  value: string;
  error?: string;
  onChange: (field: ContactField, value: string) => void;
  ids: {
    fieldId: (f: ContactField) => string;
    errorId: (f: ContactField) => string;
    hintId: (f: ContactField) => string;
  };
  type?: string;
  autoComplete?: string;
  placeholder?: string;
  hint?: string;
  required?: boolean;
  multiline?: boolean;
}) {
  const invalid = Boolean(error);
  const describedBy =
    [hint ? ids.hintId(field) : null, invalid ? ids.errorId(field) : null]
      .filter(Boolean)
      .join(" ") || undefined;

  const shared = {
    id: ids.fieldId(field),
    name: field,
    value,
    required,
    "aria-required": required || undefined,
    "aria-invalid": invalid || undefined,
    "aria-describedby": describedBy,
    maxLength: MAX_LENGTHS[field],
    placeholder,
    onChange: (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => onChange(field, e.target.value),
  };

  return (
    <div>
      <label
        htmlFor={ids.fieldId(field)}
        className="block text-xs font-semibold text-primary-foreground/75 mb-1.5"
      >
        {FIELD_LABELS[field]}
        {required && (
          <>
            {" "}
            <span aria-hidden="true">*</span>
            <span className="sr-only">required</span>
          </>
        )}
      </label>

      {hint && (
        <p id={ids.hintId(field)} className="text-xs text-primary-foreground/60 mb-1.5">
          {hint}
        </p>
      )}

      {multiline ? (
        <textarea
          {...shared}
          rows={4}
          className={`${inputClass} ${borderClass(invalid)} resize-y`}
        />
      ) : (
        <input
          {...shared}
          type={type}
          autoComplete={autoComplete}
          className={`${inputClass} ${borderClass(invalid)}`}
        />
      )}

      {invalid && (
        <p
          id={ids.errorId(field)}
          className="mt-1.5 flex items-start gap-1.5 text-xs text-error-light"
        >
          <AlertCircle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          {error}
        </p>
      )}
    </div>
  );
}
